/**
 * 브라우저 뒤로가기로 화면을 떠나려 할 때 미저장 변경을 확인받는다.
 *
 * 앱 라우터가 BrowserRouter(data router 아님)라 useBlocker 를 쓸 수 없다. 대신
 * 같은 URL 의 history 항목(센티넬)을 하나 더 쌓아 뒤로가기 한 번을 흡수하고,
 * 사용자가 확인한 뒤에 실제로 그만큼 더 뒤로 보낸다.
 *
 * 탭 닫기·새로고침은 여기서 맡을 수 없다 — 브라우저가 beforeunload 에서 자체
 * 다이얼로그만 허용하고 커스텀 UI 를 금지한다. 그쪽은 useUnsavedChangesGuard.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/** 우리가 쌓은 항목임을 popstate 에서 알아보기 위한 표식 */
const SENTINEL = { unsavedExitGuard: true } as const;

export const useUnsavedBackGuard = (
  hasUnsavedChanges: boolean,
  /** 확인 팝업을 띄운다. 사용자가 나가기를 고르면 `leave` 를 부른다 */
  confirmLeave: (leave: () => void) => void,
): void => {
  const navigate = useNavigate();
  // 리스너는 마운트당 한 번만 단다 — 편집·저장마다 센티넬이 다시 쌓이면 history 가 더럽혀진다.
  const hasChangesRef = useRef(hasUnsavedChanges);
  hasChangesRef.current = hasUnsavedChanges;
  const confirmLeaveRef = useRef(confirmLeave);
  confirmLeaveRef.current = confirmLeave;

  useEffect(() => {
    const pushSentinel = () =>
      window.history.pushState(SENTINEL, "", window.location.href);

    // 화면에 들어오기 전 항목이 있었는지. 없으면(URL 직접 진입) 뒤로 보낼 곳이 없어 홈으로 내보낸다.
    const hadPreviousEntry = window.history.length > 1;
    pushSentinel();

    // 팝업이 떠 있는 동안 뒤로가기를 또 눌러도 팝업을 겹쳐 쌓지 않는다.
    let asking = false;

    const handlePopState = () => {
      if (!hasChangesRef.current) {
        // 막을 것이 없으면 사용자가 누른 뒤로가기를 그대로 이어준다
        window.history.go(-1);
        return;
      }
      // 먼저 센티넬을 복구해 화면에 머문 상태로 묻는다
      pushSentinel();
      if (asking) return;
      asking = true;
      confirmLeaveRef.current(() => {
        asking = false;
        window.removeEventListener("popstate", handlePopState);
        // 센티넬과 이 화면 항목을 함께 넘어간다
        if (hadPreviousEntry) window.history.go(-2);
        else navigate("/");
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);
};
